import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/newStockIssueAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchIssueCCList = createAsyncThunk('newStockIssue/fetchCCList', async ({ uid, rid }, { rejectWithValue }) => {
    try { const res = await api.getIssueCCList(uid, rid); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch Cost Centres')); }
});

export const fetchIndentsByCC = createAsyncThunk('newStockIssue/fetchIndents', async (cccode, { rejectWithValue }) => {
    try { const res = await api.getIndentsByCC(cccode); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch indents')); }
});

export const fetchIndentType = createAsyncThunk('newStockIssue/fetchIndentType', async (indentno, { rejectWithValue }) => {
    try { const res = await api.getIndentType(indentno); return res?.Data?.[0]?.CheckIndentType || ''; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch indent type')); }
});

export const fetchIssueItemCodes = createAsyncThunk('newStockIssue/fetchItemCodes', async ({ cccode, indentno }, { rejectWithValue }) => {
    try { const res = await api.getIssueItemCodes(cccode, indentno); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch item codes')); }
});

export const fetchTempDetails = createAsyncThunk('newStockIssue/fetchTempDetails', async ({ itemcode, cccode, indent }, { rejectWithValue }) => {
    try { const res = await api.getTempDetails(itemcode, cccode, indent); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch item lot details')); }
});

export const saveTempItems = createAsyncThunk('newStockIssue/saveTempItems', async (payload, { rejectWithValue }) => {
    try { const res = await api.saveTempItems(payload); return res?.Data; }
    catch (e) { return rejectWithValue(err(e, 'Failed to add item to issue')); }
});

export const fetchCartGrid = createAsyncThunk('newStockIssue/fetchCartGrid', async ({ cccode, indentno }, { rejectWithValue }) => {
    try { const res = await api.getCartGrid(cccode, indentno); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch issue cart')); }
});

export const deleteCartItem = createAsyncThunk('newStockIssue/deleteCartItem', async (payload, { rejectWithValue }) => {
    try { const res = await api.deleteCartItem(payload); return res?.Data; }
    catch (e) { return rejectWithValue(err(e, 'Failed to delete item')); }
});

export const saveIssue = createAsyncThunk('newStockIssue/saveIssue', async (payload, { rejectWithValue }) => {
    try { const res = await api.saveIssue(payload); return res?.Data; }
    catch (e) { return rejectWithValue(err(e, 'Failed to submit issue')); }
});

const initialState = {
    ccList: [], indentList: [], indentType: '', indentTypeLoading: false,
    itemCodeList: [], itemCodeListLoading: false,
    suggestions: [],
    tempItems: [], tempLoading: false,
    cartItems: [], cartLoading: false,
    loading: { ccList: false, indentList: false, saveTemp: false, delete: false, submit: false },
    errors: { ccList: null, indentList: null, indentType: null, itemCodes: null, temp: null, saveTemp: null, cart: null, delete: null, submit: null },
    saveTempResult: null,
    submitResult: null,
};

const newStockIssueSlice = createSlice({
    name: 'newStockIssue',
    initialState,
    reducers: {
        clearIndentType: (s) => { s.indentType = ''; },
        clearItemCodeList: (s) => { s.itemCodeList = []; },
        clearTempItems: (s) => { s.tempItems = []; s.errors.temp = null; },
        clearSaveTempResult: (s) => { s.saveTempResult = null; s.errors.saveTemp = null; },
        clearCartGrid: (s) => { s.cartItems = []; },
        clearSubmitResult: (s) => { s.submitResult = null; s.errors.submit = null; },
        resetNewStockIssue: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIssueCCList.pending, (s) => { s.loading.ccList = true; })
            .addCase(fetchIssueCCList.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchIssueCCList.rejected, (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchIndentsByCC.pending, (s) => { s.loading.indentList = true; })
            .addCase(fetchIndentsByCC.fulfilled, (s, a) => { s.loading.indentList = false; s.indentList = a.payload; })
            .addCase(fetchIndentsByCC.rejected, (s, a) => { s.loading.indentList = false; s.errors.indentList = a.payload; });

        builder
            .addCase(fetchIndentType.pending, (s) => { s.indentTypeLoading = true; s.errors.indentType = null; })
            .addCase(fetchIndentType.fulfilled, (s, a) => { s.indentTypeLoading = false; s.indentType = a.payload; })
            .addCase(fetchIndentType.rejected, (s, a) => { s.indentTypeLoading = false; s.errors.indentType = a.payload; });

        builder
            .addCase(fetchIssueItemCodes.pending, (s) => { s.itemCodeListLoading = true; })
            .addCase(fetchIssueItemCodes.fulfilled, (s, a) => { s.itemCodeListLoading = false; s.itemCodeList = a.payload; })
            .addCase(fetchIssueItemCodes.rejected, (s, a) => { s.itemCodeListLoading = false; s.errors.itemCodes = a.payload; });

        builder
            .addCase(fetchTempDetails.pending, (s) => { s.tempLoading = true; s.errors.temp = null; })
            .addCase(fetchTempDetails.fulfilled, (s, a) => { s.tempLoading = false; s.tempItems = a.payload; })
            .addCase(fetchTempDetails.rejected, (s, a) => { s.tempLoading = false; s.errors.temp = a.payload; s.tempItems = []; });

        builder
            .addCase(saveTempItems.pending, (s) => { s.loading.saveTemp = true; s.errors.saveTemp = null; })
            .addCase(saveTempItems.fulfilled, (s, a) => { s.loading.saveTemp = false; s.saveTempResult = a.payload; })
            .addCase(saveTempItems.rejected, (s, a) => { s.loading.saveTemp = false; s.errors.saveTemp = a.payload; });

        builder
            .addCase(fetchCartGrid.pending, (s) => { s.cartLoading = true; s.errors.cart = null; })
            .addCase(fetchCartGrid.fulfilled, (s, a) => { s.cartLoading = false; s.cartItems = a.payload; })
            .addCase(fetchCartGrid.rejected, (s, a) => { s.cartLoading = false; s.errors.cart = a.payload; s.cartItems = []; });

        builder
            .addCase(deleteCartItem.pending, (s) => { s.loading.delete = true; s.errors.delete = null; })
            .addCase(deleteCartItem.fulfilled, (s) => { s.loading.delete = false; })
            .addCase(deleteCartItem.rejected, (s, a) => { s.loading.delete = false; s.errors.delete = a.payload; });

        builder
            .addCase(saveIssue.pending, (s) => { s.loading.submit = true; s.errors.submit = null; })
            .addCase(saveIssue.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; })
            .addCase(saveIssue.rejected, (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearIndentType, clearItemCodeList, clearTempItems, clearSaveTempResult, clearCartGrid, clearSubmitResult, resetNewStockIssue,
} = newStockIssueSlice.actions;

export const selectNSICCList = (s) => s.newStockIssue.ccList;
export const selectNSIIndentList = (s) => s.newStockIssue.indentList;
export const selectNSIIndentType = (s) => s.newStockIssue.indentType;
export const selectNSIItemCodeList = (s) => s.newStockIssue.itemCodeList;
export const selectNSITempItems = (s) => s.newStockIssue.tempItems;
export const selectNSITempLoading = (s) => s.newStockIssue.tempLoading;
export const selectNSICartItems = (s) => s.newStockIssue.cartItems;
export const selectNSICartLoading = (s) => s.newStockIssue.cartLoading;
export const selectNSILoading = (s) => s.newStockIssue.loading;
export const selectNSIErrors = (s) => s.newStockIssue.errors;
export const selectNSISubmitResult = (s) => s.newStockIssue.submitResult;

export default newStockIssueSlice.reducer;
